import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './Context/AuthContext';
import { favoriteService } from './services/favoriteService';
import { cartService } from './services/cartService';

import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Gallery from './pages/Gallery';
import ShoppingCart from './pages/ShoppingCart';
import Favourites from './pages/Favourites';
import Checkout from './pages/Checkout';
import AdminDashboard from './pages/AdminDashboard';
import TrackingDetails from './pages/TrackingDetails';
import CustomerOrderDetails from './pages/CustomerOrderDetails';
import ForgotPassword from './pages/ForgotPassword';
import EditProductPage from './pages/EditProductPage';
//import ResetPassword from './pages/ResetPassword';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return user.role === 'admin'
    ? children
    : <Navigate to="/" replace />;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return user.role === 'admin' ? <Navigate to="/admin" /> : <Navigate to="/home" />;
  }
  return children;
};

function AppContent() {
    const { user } = useAuth();

    // --- Cart State ---
    const [cartItems, setCartItems] = useState([]);

    /**
     * Normalize API cart items to match the shape expected by ShoppingCart.js.
     * Maps server fields (product_name, product_price, etc.) to UI fields.
     */
    const normalizeCartItems = (items) =>
      items.map((item) => ({
        id: item.product_id,                                        // product identifier (used as key + for dedup)
        cart_item_id: item.id,                                      // DB row id (used for PATCH/DELETE API calls)
        title: item.product_name,
        image: item.product_image,
        category: item.product_category,
        price: `Rs ${parseFloat(item.product_price).toFixed(2)}`,   // format matches ShoppingCart price parser
        quantity: item.quantity,
      }));

    const fetchCart = async () => {
      if (user?.id) {
        try {
          const result = await cartService.getCart(user.id);
          if (result.success) {
            setCartItems(normalizeCartItems(result.data.items));
          }
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      } else {
        setCartItems([]);
      }
    };

    // Fetch cart whenever the logged-in user changes
    useEffect(() => {
      fetchCart();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const clearCart = async () => {
      if (!user) return;
      setCartItems([]);
      try {
        await cartService.clearCart(user.id);
      } catch (error) {
        console.error('Error clearing cart:', error);
        await fetchCart(); // rollback
      }
    };

    const addToCart = async (product) => {
      if (!user) return;
      // Optimistic update — add immediately to UI
      setCartItems((prevItems) => {
        const existingItem = prevItems.find((item) => item.id === product.id);
        if (existingItem) {
          return prevItems.map((item) =>
            item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
          );
        }
        return [...prevItems, { ...product, quantity: 1 }];
      });
      try {
        const result = await cartService.addItem(user.id, product.id, 1);
        if (result.success) {
          await fetchCart(); // sync with server to get the real cart_item_id
        } else {
          await fetchCart(); // rollback
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        await fetchCart(); // rollback
      }
    };

    const removeFromCart = async (productId) => {
      const item = cartItems.find((i) => i.id === productId);
      if (!item) return;
      // Optimistic update
      setCartItems((prevItems) => prevItems.filter((i) => i.id !== productId));
      try {
        await cartService.removeItem(item.cart_item_id);
      } catch (error) {
        console.error('Error removing from cart:', error);
        await fetchCart(); // rollback
      }
    };

    const updateQuantity = async (productId, newQuantity) => {
      if (newQuantity < 1) return;
      const item = cartItems.find((i) => i.id === productId);
      if (!item) return;
      // Optimistic update
      setCartItems((prevItems) =>
        prevItems.map((i) => (i.id === productId ? { ...i, quantity: newQuantity } : i))
      );
      try {
        await cartService.updateItem(item.cart_item_id, newQuantity);
      } catch (error) {
        console.error('Error updating quantity:', error);
        await fetchCart(); // rollback
      }
    };

    // --- Favourites State ---
    const [savedItems, setSavedItems] = useState([]);

    const fetchFavorites = async () => {
      if (user?.id) {
        try {
          const result = await favoriteService.getFavorites(user.id);
          if (result.success) {
            setSavedItems(result.data);
          }
        } catch (error) {
          console.error('Error fetching favorites:', error);
        }
      } else {
        setSavedItems([]);
      }
    };

    useEffect(() => {
      fetchFavorites();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const addFavorite = async (design) => {
      if (!user) return;
      // Optimistic update
      setSavedItems(prev => [...prev, { id: `temp-${design.id}`, product_id: design.id }]);
      try {
        const result = await favoriteService.addFavorite(user.id, design.id);
        if (result.success) {
          await fetchFavorites(); // replace temp entry with real server data
        } else {
          await fetchFavorites(); // rollback
        }
      } catch (err) {
        console.error('Error adding favorite:', err);
        await fetchFavorites(); // rollback
      }
    };

    const removeFavorite = async (design) => {
      const savedItem = savedItems.find(item => item.product_id === design.id);
      if (!savedItem) return;
      // Optimistic update
      setSavedItems(prev => prev.filter(item => item.product_id !== design.id));
      try {
        const result = await favoriteService.removeFavorite(savedItem.id);
        if (!(result === '' || result?.success)) {
          await fetchFavorites(); // rollback on unexpected response
        }
      } catch (err) {
        console.error('Error removing favorite:', err);
        await fetchFavorites(); // rollback
      }
    };
  return (
    <Routes>
      {/* ✅ PUBLIC ROUTES */}
<Route
  path="/"
  element={
    <Home
      cartItems={cartItems}
      savedItems={savedItems}
      addToCart={addToCart}
      addFavorite={addFavorite}
      removeFavorite={removeFavorite}
    />
} />
        <Route
          path="/home"
          element={
              <Home      cartItems={cartItems}
                         savedItems={savedItems}
                         addToCart={addToCart}
                         addFavorite={addFavorite}
                         removeFavorite={removeFavorite} />
          }
        />
<Route
  path="/gallery"
  element={
    <Gallery
      cartItems={cartItems}
      savedItems={savedItems}
      addToCart={addToCart}
      addFavorite={addFavorite}
      removeFavorite={removeFavorite}
    />
  }
/>

      {/* ✅ AUTH SCREENS */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* ✅ PROTECTED PAGES */}
  <Route
          path="/cart"
          element={
            <ProtectedRoute>
              <ShoppingCart
                cartItems={cartItems}
                removeFromCart={removeFromCart}
                updateQuantity={updateQuantity}
              />
            </ProtectedRoute>
          }
        />

      <Route
             path="/favourites"
             element={
               <ProtectedRoute>
                 <Favourites
                   savedItems={savedItems}
                   removeFavorite={removeFavorite}
                   addToCart={addToCart}
                   cartItems={cartItems}
                 />
               </ProtectedRoute>
             }
           />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout clearCart={clearCart} />
            </ProtectedRoute>
          }
        />

    <Route
           path="/tracking"
           element={
             <ProtectedRoute>
               <TrackingDetails />
             </ProtectedRoute>
           }
         />

      <Route
        path="/my-orders/:id"
        element={
          <ProtectedRoute>
            <CustomerOrderDetails />
          </ProtectedRoute>
        }
      />

      {/* ✅ ADMIN */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/products/edit/:id"
        element={
          <AdminRoute>
            <EditProductPage />
          </AdminRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
